// POSMenu — overflow menu for the POS top bar. Hosts the "less-used"
// actions (back to admin, end shift) so the top bar doesn't get
// cluttered. The frequently-used actions (Transaksi, Stok, Draft) get
// their own inline buttons in the top bar — this menu is the secondary
// slot.
//
// Why a menu (not buttons): the POS top bar is already crowded with
// event context, cashier name, end-shift, draft count, etc. Stuffing
// 4-5 action buttons in there breaks the visual hierarchy. The menu
// keeps the bar scannable and gives the overflow actions a single
// touch target.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  MoreHorizontal,
  Home,
  PowerOff,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

export interface POSMenuProps {
  /** Trigger render. If absent, the trigger button itself is hidden. */
  cashierName: string;
  eventName: string;
  shiftStartedAt: number;
  shiftTransactions: number;
  shiftTotalSales: number;
  onEndShift: () => void;
}

export function POSMenu({
  cashierName,
  eventName,
  shiftStartedAt,
  shiftTransactions,
  shiftTotalSales,
  onEndShift,
}: POSMenuProps) {
  const [open, setOpen] = useState(false);
  const [endShiftOpen, setEndShiftOpen] = useState(false);
  const [adminConfirmOpen, setAdminConfirmOpen] = useState(false);
  const navigate = useNavigate();

  const handleBackToAdmin = () => {
    setOpen(false);
    setAdminConfirmOpen(true);
  };

  const handleConfirmBackToAdmin = () => {
    setAdminConfirmOpen(false);
    // Preserve the shift state in localStorage (already persisted by
    // the zustand store). The POSGate route guard will read it on
    // the next visit to /pos and skip the shift-start screen.
    navigate('/dashboard');
  };

  const handleEndShift = () => {
    setOpen(false);
    setEndShiftOpen(true);
  };

  const handleConfirmEndShift = () => {
    setEndShiftOpen(false);
    onEndShift();
    navigate('/dashboard');
  };

  return (
    <>
      <div className="relative">
        <Button
          variant="ghost"
          size="md"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu POS lainnya"
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <MoreHorizontal size={16} />
        </Button>

        {open && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-30 cursor-default"
              onClick={() => setOpen(false)}
              aria-label="Tutup menu"
            />
            <div
              role="menu"
              className="absolute right-0 mt-2 w-64 z-40 bg-surface border border-line rounded-lg shadow-hover py-1.5 overflow-hidden"
            >
              <MenuItem
                icon={<Home size={15} />}
                label="Kembali ke admin"
                description="Lihat dashboard tanpa akhiri shift"
                onClick={handleBackToAdmin}
              />
              <div className="my-1 border-t border-line" />
              <MenuItem
                icon={<LogOut size={15} />}
                label="Akhiri shift"
                description="Reset cart, kembali ke admin"
                onClick={handleEndShift}
                tone="danger"
              />
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={adminConfirmOpen}
        onClose={() => setAdminConfirmOpen(false)}
        title="Kembali ke admin?"
        description="Shift kamu tetap aktif. Kamu bisa balik ke POS kapan aja dari dashboard atau menu POS."
        confirmText="Kembali"
        onConfirm={handleConfirmBackToAdmin}
      />

      <EndShiftDialog
        open={endShiftOpen}
        onClose={() => setEndShiftOpen(false)}
        onConfirm={handleConfirmEndShift}
        cashierName={cashierName}
        eventName={eventName}
        shiftStartedAt={shiftStartedAt}
        transactions={shiftTransactions}
        totalSales={shiftTotalSales}
      />
    </>
  );
}

function MenuItem({
  icon,
  label,
  description,
  onClick,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onClick: () => void;
  tone?: 'default' | 'danger';
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`w-full px-3 py-2.5 flex items-start gap-3 text-left hover:bg-canvas/70 transition-colors ${
        tone === 'danger' ? 'text-danger hover:bg-danger/5' : 'text-ink'
      }`}
    >
      <span
        className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${
          tone === 'danger' ? 'bg-danger/10' : 'bg-primary/10 text-primary'
        }`}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-small font-medium">{label}</span>
        {description && (
          <span className="block text-caption text-ink-subtle mt-0.5">
            {description}
          </span>
        )}
      </span>
    </button>
  );
}

function EndShiftDialog({
  open,
  onClose,
  onConfirm,
  cashierName,
  eventName,
  shiftStartedAt,
  transactions,
  totalSales,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  cashierName: string;
  eventName: string;
  shiftStartedAt: number;
  transactions: number;
  totalSales: number;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Akhiri shift?"
      description={`Ringkasan shift ${cashierName}.`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Lanjut shift
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            rightIcon={<PowerOff size={16} />}
          >
            Akhiri & kembali ke admin
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="p-3 rounded bg-canvas border border-line space-y-1">
          <p className="text-caption text-ink-subtle">Event</p>
          <p className="text-body font-medium text-ink">{eventName}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <StatBox
            label="Mulai"
            value={
              shiftStartedAt
                ? new Date(shiftStartedAt).toLocaleString('id-ID', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '—'
            }
          />
          <StatBox
            label="Durasi"
            value={(() => {
              const ms = Date.now() - shiftStartedAt;
              const h = Math.floor(ms / 3_600_000);
              const m = Math.floor((ms % 3_600_000) / 60_000);
              return `${h} jam ${m} menit`;
            })()}
          />
          <StatBox label="Transaksi" value={String(transactions)} />
          <StatBox
            label="Penjualan"
            value={`Rp ${totalSales.toLocaleString('id-ID')}`}
          />
        </div>
        <div className="p-3 rounded border border-warning/30 bg-warning/5 text-caption text-warning">
          Cart akan di-reset setelah shift berakhir. Event tetap aktif (tidak di-close
          otomatis) — close manual dari halaman Events. Draft yang tersimpan akan tetap
          ada untuk akun kamu.
        </div>
      </div>
    </Modal>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded bg-canvas border border-line">
      <p className="text-overline uppercase text-ink-subtle font-medium">
        {label}
      </p>
      <p className="text-body font-display font-semibold text-ink mt-0.5">
        {value}
      </p>
    </div>
  );
}
